
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { getCurrencyAndRate } from 'components/Orders/OrdersDesktop';
import { addCommasToNumber } from 'utils/utils';

const useOrderExport = ({tableData}) => {

      // Function to prepare data for export
      const prepareExportData = (selectedOnly = false, order) => {
          const dataToExport = selectedOnly ? [order] : tableData;
          return dataToExport.map((order, i) => {
              const shipmentDetails = JSON.parse(order.shipment_details || '{}');
              const orderDetails = JSON.parse(order.order_details || '[]');
              const { finalCurrency, finalRate } = getCurrencyAndRate(order);
              // Calculate totals
              const subTotal = orderDetails.reduce((sum, item) => sum + (item.total_price || 0), 0);
              const itemTotal = subTotal * finalRate;
              const shippingCost =
                  order?.amount && order?.amount > itemTotal
                      ? order?.amount - itemTotal
                      : order.shipping_fee || 0;
              const tax = order.tax || 0;
              const discount = order.discount || 0;
  
              const grandTotal = order?.amount
                  ? order?.amount
                  : itemTotal + shippingCost + tax - discount;
  
              return {
                  'Order ID': `${order?.order_id || i}`,
                  'Date Ordered': new Date(order.createdAt).toLocaleString(),
                  'Customer Name':
                      `${shipmentDetails?.delivery_address?.first_name ?? ''} ${
                          shipmentDetails?.delivery_address?.last_name ?? ''
                      }`.trim() || 'N/A',
                  'Customer Email': shipmentDetails?.delivery_address?.email ?? 'N/A',
                  'Payment Status': order?.payments || 'N/A',
                  'Order Status': order.status?.toUpperCase() ?? 'PENDING',
                  'Item Total': `${finalCurrency} ${addCommasToNumber(itemTotal?.toFixed(2))}`,
                  'Shipping Cost': `${finalCurrency} ${shippingCost.toFixed(2)}`,
                  Tax: `${finalCurrency} ${tax.toFixed(2)}`,
                  Discount: discount > 0 ? `${finalCurrency} ${discount.toFixed(2)}` : 'N/A',
                  'Grand Total': order?.orderAmount ?? `${finalCurrency} ${grandTotal.toFixed(2)}`,
                  'Commission Payable':
                      order?.orderCommission ??
                      `${finalCurrency} ${(order.commission || 0).toFixed(2)}`,
                  Currency: finalCurrency,
              };
          });
      };
  
      const exportAsCSV = (selectedOnly = false, order) => {
          const data = prepareExportData(selectedOnly, order);
          const headers = Object.keys(data[0]);
          const customerName = data?.[0]?.['Customer Name'];
          const timestamp = new Date().toLocaleString();
  
          const csvContent = [
              headers.join(','),
              ...data.map((row) => headers.map((header) => `"${row[header]}"`).join(',')),
              '',
              `"Generated from Obana on ${timestamp}"`,
          ].join('\n');
  
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          saveAs(blob, `orders_${order?.order_ref}_${customerName}.csv`);
      };
  
      // Export as XLSX
      const exportAsXLSX = (selectedOnly = false, order) => {
          const data = prepareExportData(selectedOnly, order);
          const customerName = data?.[0]?.['Customer Name'];
          const timestamp = new Date().toLocaleString();
  
          // Add footer row
          const dataWithFooter = [
              ...data,
              {},
              { 'Order ID': `Generated from Obana on ${timestamp}` },
          ];
  
          const ws = XLSX.utils.json_to_sheet(dataWithFooter);
          const wb = XLSX.utils.book_new();
  
          // Set column widths for better readability
          ws['!cols'] = [
              { wch: 20 },
              { wch: 20 },
              { wch: 20 },
              { wch: 25 },
              { wch: 15 },
              { wch: 15 },
              { wch: 15 },
              { wch: 15 },
              { wch: 15 },
              { wch: 15 },
              { wch: 15 },
              { wch: 15 },
          ];
  
          XLSX.utils.book_append_sheet(wb, ws, 'Orders');
          XLSX.writeFile(wb, `orders_${order?.order_ref}_${customerName}.xlsx`);
      };
  
      // Export as PDF with enhanced invoice format
      const exportAsPDF = (selectedOnly = false, order, salespersonName = 'Admin') => {
          const data = prepareExportData(selectedOnly, order);
          const customerName = data?.[0]?.['Customer Name'];
          const timestamp = new Date().toLocaleString();
          const orderDetails = JSON.parse(order.order_details || '[]');
          const shipmentDetails = JSON.parse(order.shipment_details || '{}');
          const { finalCurrency, finalRate } = getCurrencyAndRate(order);
  
          const doc = new jsPDF();
          const pageWidth = doc.internal.pageSize.getWidth();
  
          // Header with company info
          doc.setFontSize(18);
          doc.setFont(undefined, 'bold');
          doc.text('OBANA', 14, 20);
  
          doc.setFontSize(9);
          doc.setFont(undefined, 'normal');
          doc.text('Order Invoice', 14, 27);
          doc.text('Contact: contact@obana.africa', 14, 32);
          doc.text(`Generated: ${timestamp}`, pageWidth - 14, 20, { align: 'right' });
          doc.text(`Order Ref: ${order?.order_ref || 'N/A'}`, pageWidth - 14, 27, { align: 'right' });
  
          // Line separator
          doc.setDrawColor(200);
          doc.line(14, 36, pageWidth - 14, 36);
  
          // Customer details
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.text('Bill To:', 14, 44);
          doc.setFont(undefined, 'normal');
          doc.setFontSize(9);
          doc.text(customerName, 14, 50);
          doc.text(shipmentDetails?.delivery_address?.email || 'N/A', 14, 55);
          doc.text(shipmentDetails?.delivery_address?.phone || 'N/A', 14, 60);
  
          // Order items table
          const itemsData = orderDetails.map((item) => [
              item.name || 'N/A',
              item.quantity || 1,
              `${finalCurrency} ${(
                  ((item.total_price || item.rate) * finalRate || 0) / (item.quantity || 1)
              ).toFixed(2)}`,
              `${finalCurrency} ${((item.total_price || item.rate) * finalRate || 0).toFixed(2)}`,
          ]);
  
          doc.autoTable({
              startY: 68,
              head: [['Item Description', 'Boxes', 'Cost per Box', 'Total']],
              body: itemsData,
              styles: { fontSize: 9 },
              headStyles: { fillColor: [27, 59, 95], fontStyle: 'bold' },
              theme: 'striped',
          });
  
          // Summary section
          let finalY = doc.lastAutoTable.finalY + 10;
          const summaryX = pageWidth - 70;
          console.log({ orderDetails });
  
          const subTotal = orderDetails.reduce((sum, item) => sum + (item.total_price || 0), 0);
          const itemTotal = subTotal * finalRate;
          const shippingCost = order.shipping_fee || 0;
          const tax = order.tax || 0;
          const discount = order.discount || 0;
          console.log({ shippingCost, itemTotal, subTotal, finalRate });
          const grandTotal = itemTotal + shippingCost + tax - discount;
  
          doc.setFontSize(9);
          doc.text('Item Total:', summaryX, finalY);
          doc.text(`${finalCurrency} ${itemTotal.toFixed(2)}`, pageWidth - 14, finalY, {
              align: 'right',
          });
  
          finalY += 6;
          doc.text('Shipping:', summaryX, finalY);
          doc.text(`${finalCurrency} ${shippingCost.toFixed(2)}`, pageWidth - 14, finalY, {
              align: 'right',
          });
  
          if (tax > 0) {
              finalY += 6;
              doc.text('Tax:', summaryX, finalY);
              doc.text(`${finalCurrency} ${tax.toFixed(2)}`, pageWidth - 14, finalY, {
                  align: 'right',
              });
          }
  
          if (discount > 0) {
              finalY += 6;
              doc.text('Discount:', summaryX, finalY);
              doc.text(`-${finalCurrency} ${discount.toFixed(2)}`, pageWidth - 14, finalY, {
                  align: 'right',
              });
          }
  
          finalY += 8;
          doc.setFont(undefined, 'bold');
          doc.setFontSize(11);
          doc.text('Grand Total:', summaryX, finalY);
          doc.text(`${finalCurrency} ${grandTotal.toFixed(2)}`, pageWidth - 14, finalY, {
              align: 'right',
          });
  
          // Footer
          const pageHeight = doc.internal.pageSize.getHeight();
          doc.setFontSize(8);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(128);
          doc.text(
              `Generated from Obana on ${timestamp} by ${salespersonName}`,
              pageWidth / 2,
              pageHeight - 10,
              { align: 'center' },
          );
  
          doc.save(`invoice_${order?.order_ref}_${customerName}.pdf`);
      };
  return {exportAsCSV, exportAsPDF, exportAsXLSX};
}

export default useOrderExport;